import LoadingPage from '@/components/templates/LoadingPage';
import SidebarItemViewTemplate from '@/components/templates/SidebarItemViewTemplate';
import { Badge, HStack, Select, Text, useDisclosure } from '@chakra-ui/react';
import { createFileRoute } from '@tanstack/react-router';
import { useLegalDocuments } from '@/hooks/legal/legal';
import CreateLegalDocumentModal from '@/modules/legal/CreateLegalDocumentModal/CreateLegalDocumentModal';
import { TLegalDocument, TLegalDocumentType } from '@/types/Legal.types';
import CustomTable from '@/components/molecules/CustomTable';
import { LEGAL_DOCUMENT_TYPES, LEGAL_DOCUMENT_TYPE_LABELS } from '@/constants/stringVars';
import { useMemo, useState } from 'react';

const COLUMNS = ['Type', 'Version', 'Is Active', 'Created At'];
type TSortingColumn = (typeof COLUMNS)[number];

type TStatusFilter = '' | 'active' | 'inactive';

const selectStyles = {
  bg: 'white',
  borderColor: 'background.separatorBlue',
  borderWidth: '1px',
  borderRadius: '8px',
  fontSize: '14px',
  color: 'text.darkBlue',
  cursor: 'pointer',
  _hover: { borderColor: 'primary.300' },
  _focusVisible: { borderColor: 'primary.500', boxShadow: 'none' },
} as const;

const typeLabel = (type: TLegalDocument['type']) =>
  LEGAL_DOCUMENT_TYPE_LABELS[type as TLegalDocumentType] ?? type?.replaceAll('_', ' ');

const EulaDocuments = () => {
  const { isOpen: isCreateModalOpen, onClose: onCreateModalClose, onOpen: onCreateModalOpen } = useDisclosure();

  const [typeFilter, setTypeFilter] = useState<TLegalDocumentType | ''>('');
  const [statusFilter, setStatusFilter] = useState<TStatusFilter>('');

  const {
    isLoading,
    data: documents,
    pagination,
    isRefetching,
  } = useLegalDocuments({
    type: typeFilter || undefined,
    status: statusFilter || undefined,
  });

  // Filtering changes the result set, so jump back to the first page.
  const onTypeFilterChange = (value: TLegalDocumentType | '') => {
    setTypeFilter(value);
    pagination.getFirstPage();
  };

  const onStatusFilterChange = (value: TStatusFilter) => {
    setStatusFilter(value);
    pagination.getFirstPage();
  };

  const documentsTableData = documents?.map((document) => ({
    ...document,
    id: document._id,
  }));

  const data = { nodes: documentsTableData ?? [] };

  const tableListItems = useMemo(
    () =>
      data.nodes.map((item: (typeof data.nodes)[number]) => {
        return {
          id: item.id,
          cells: [
            typeLabel(item.type),
            <Text marginLeft={'10px'} fontWeight={'bold'}>
              {item.version}
            </Text>,
            <Badge marginLeft={'12px'} colorScheme={item.isActive ? 'green' : 'gray'}>
              {item.isActive ? 'Active' : 'Inactive'}
            </Badge>,
            item.createdAt,
          ],
        };
      }),
    [data.nodes],
  );

  if (isLoading) return <LoadingPage />;

  return (
    <SidebarItemViewTemplate
      title={'Legal Documents'}
      buttonLabel="Create New Document"
      onButtonClick={onCreateModalOpen}
    >
      <HStack
        width={'100%'}
        justifyContent={'flex-start'}
        alignItems={'flex-end'}
        spacing={'16px'}
        marginBottom={'16px'}
        bg={'background.lightBlue'}
      >
        <Select
          width={'240px'}
          value={typeFilter}
          onChange={(e) => onTypeFilterChange(e.target.value as TLegalDocumentType | '')}
          aria-label="Filter by type"
          {...selectStyles}
        >
          <option value="">All types</option>
          {LEGAL_DOCUMENT_TYPES.map((type) => (
            <option key={type} value={type}>
              {LEGAL_DOCUMENT_TYPE_LABELS[type]}
            </option>
          ))}
        </Select>
        <Select
          width={'180px'}
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as TStatusFilter)}
          aria-label="Filter by status"
          {...selectStyles}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </Select>
      </HStack>

      <CustomTable<TLegalDocument[], TSortingColumn>
        noItemsMessage="No documents match the selected filters"
        columns={COLUMNS}
        dataWithId={data.nodes}
        isLoading={isLoading}
        isRefetching={isRefetching}
        pagination={pagination}
        tableListItems={tableListItems}
        customColumnGridPercentages={[30, 20, 20, 30]}
        hasRowSelect
      />

      <CreateLegalDocumentModal isOpen={isCreateModalOpen} onClose={onCreateModalClose} />
    </SidebarItemViewTemplate>
  );
};

export const Route = createFileRoute('/_authenticated/_admin/eula/')({
  component: EulaDocuments,
});
